﻿<template>
  <loading-placeholder v-if="isLoading" :lines="1" class="item-filters-panel__loading" />
  <div v-else class="item-filters-panel">
    <div v-if="isDisabled" class="item-filters-panel__disabler" />
    <!-- Tablet/Desktop -->
    <div class="item-filters-panel__filters-wrapper marker--items-filters-panel">
    
      <!-- Title / Ref -->
      <ifp-filter-title-ref-search
        :search-term="searchTerm"
        @search-term-updated="(val) => $emit('update:searchTerm', val)"
      />
      <!-- Entities -->
      <ifp-filter-entities
        v-if="isAll"
        :selected-ids="selectedEntityIds"
        :entity-type="allEntityType as EEntities"
        :entity-options="entityOptions as IProjectForSelection[]"
        @selected-ids-updated="(ids: number[]) => $emit('update:selectedEntityIds', SortAssistant.sortChronologically(ids))"
      />
      <!-- Assignees -->
      <ifp-filter-assignees
        :selected-ids="selectedAssigneeIds"
        :assignee-options="assigneeOptions"
        :assigned-not-to-you="assignedNotToYou"
        @selected-ids-updated="(ids: number[]) => $emit('update:selectedAssigneeIds', SortAssistant.sortChronologically(ids))"
        @assigned-not-to-you-updated="(val) => $emit('update:assignedNotToYou', val)"
      />
      <!-- Jobs -->
      <ifp-filter-jobs 
        v-if="hasJobs" 
        :job-options="jobOptions" 
        :appointee-options="assigneeOptions"
        :selected-job-appointee-groupings-string="selectedJobAppointeeGroupingsString"
        @selected-job-appointee-groupings-string-updated="(val) => $emit('update:selectedJobAppointeeGroupingsString', val)"
      />
      <!-- Creators -->
      <ifp-filter-creators
        v-if="isSingleProject || isAllTasks"
        :project-id="singleProjectId"
        :selected-ids="selectedCreatorIds"
        @selected-ids-updated="(ids: number[]) => $emit('update:selectedCreatorIds', SortAssistant.sortChronologically(ids))"
      />
      <!-- Customers -->
      <ifp-filter-customers
        v-if="isSingleMailbox || isAllConvos"
        :mailbox-id="singleMailboxId"
        :selected-ids="selectedCustomerIds"
        @selected-ids-updated="(ids: number[]) => $emit('update:selectedCustomerIds', SortAssistant.sortChronologically(ids))"
      />
      <!-- Statuses -->
      <ifp-filter-statuses
        :selected-ids="selectedStatusIds"
        :status-type="statusType"
        :active-status-options="activeStatusOptions"
        :inactive-status-options="inactiveStatusOptions"
        @status-type-updated="(val) => $emit('update:statusType', val)"
        @selected-ids-updated="(ids: number[]) => $emit('update:selectedStatusIds', SortAssistant.sortChronologically(ids))"
      />
      <!-- Priorities -->
      <ifp-filter-priorities
        :selected-ids="selectedPriorityIds"
        :priority-options="priorityOptions"
        @selected-ids-updated="(ids: number[]) => $emit('update:selectedPriorityIds', SortAssistant.sortChronologically(ids))"
      />
      <!-- Due -->
      <ifp-filter-due
        :selected-due-type="selectedDueType"
        :start-due-date="startDueDate"
        :end-due-date="endDueDate"
        :due-date-options="dueDateOptions"
        @selected-due-type-updated="(val) => $emit('update:selectedDueType', val)"
        @start-due-date-updated="(val) => $emit('update:startDueDate', val)"
        @end-due-date-updated="(val) => $emit('update:endDueDate', val)"
      />
      <!-- Tags -->
      <ifp-filter-tags
        :selected-including-tag-ids="selectedIncludingTagIds"
        :selected-excluding-tag-ids="selectedExcludingTagIds"     
        :tag-options="tagOptions"
        @selected-including-tag-ids-updated="(ids) => $emit('update:selectedIncludingTagIds', SortAssistant.sortChronologically(ids))"
        @selected-excluding-tag-ids-updated="(ids) => $emit('update:selectedExcludingTagIds', SortAssistant.sortChronologically(ids))"
      />
      <!-- Custom fields: shown once picked from the "Add filter" menu or when a filter is already applied -->
      <ifp-filter-custom-field
        v-for="field in exposedCustomFields"
        :key="`custom-field-${field.id}`"
        :field="field"
        :filter="customFieldFilterFor(field)"
        @filter-updated="(filter) => updateCustomFieldFilter(field, filter)"
        @removed="removeCustomFieldFilter(field)"
      />
      <!-- Spam -->
      <ifp-filter-spam
        v-if="state.showFilterSpam"
        @spam-updated="$emit('update:isSpam', false)"
      />
      <!-- Watching -->
      <ifp-filter-watching
        v-if="state.showFilterWatching"
        @only-watching-updated="$emit('update:onlyWatching', false)"
      />
      <!-- Created within days -->
      <ifp-filter-created-within-days
        v-if="state.showFilterCreatedWithinDays"
        :created-within-days="createdWithinDays"
        @created-within-days-updated="(val) => $emit('update:createdWithinDays', val)"
      />
      <!-- Last updated within days -->
      <ifp-filter-last-updated-within-days 
        v-if="state.showFilterLastUpdatedWithinDays"
        :last-updated-within-days="lastUpdatedWithinDays"
        @last-updated-within-days-updated="(val) => $emit('update:lastUpdatedWithinDays', val)"
      />
      <ifp-picker
        v-model:show-filter-spam="state.showFilterSpam"
        v-model:show-filter-watching="state.showFilterWatching"
        v-model:show-filter-created-within-days="state.showFilterCreatedWithinDays"
        v-model:show-filter-last-updated-within-days="state.showFilterLastUpdatedWithinDays"
        :is-mailbox="isSingleMailbox || isAllConvos"
        :custom-field-options="unexposedCustomFields"
        @spam-selected="$emit('update:isSpam', true)"
        @only-watching-selected="$emit('update:onlyWatching', true)"
        @custom-field-selected="(fieldID) => exposeCustomField(fieldID)"
      />
      <template v-if="!isDefaultItemTraitSet">
        <!-- Reset filters button -->
        <dd-button
          type="text"
          class="clear-filters-button"
          @click="handleFilterReset()"
        >Clear</dd-button>
      </template>
    </div>
    <div class="other-options">
      <!-- Sort -->
      <ifp-filter-sort
        v-if="state.showFilterSort"
        :selected-sort="selectedSort"
        :is-kanban="isKanban"
        :show-switch-to-kanban="(isSingleProject || isSingleMailbox) && !isKanban && $screen.isTabletOrMobile($mq)"
        :show-switch-to-list="(isSingleProject || isSingleMailbox) && isKanban && $screen.isTabletOrMobile($mq)"
        @sort-updated="(val) => $emit('update:selectedSort', val)"
        @switch-to-kanban="$emit('switch-to-kanban')"
        @switch-to-list="$emit('switch-to-list')"
        @settings="$emit('settings')"
      />
      <!-- Slot for mobile kanban dropdown (Switch to List) -->
      <slot v-if="isKanban" name="mobile-kanban-options" />
      <project-listing-kanban-toggle
        v-if="(isSingleProject || isSingleMailbox) && $screen.isTabletOrDesktop($mq)"
        :is-kanban="isKanban"
        :is-mailbox="isSingleMailbox"
      />
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import SortAssistant from '@/helpers/sort-assistant'
import CustomFieldAssistant, {
  ICustomFieldFilter,
} from '@/helpers/custom-field-assistant'

/* Data types */
import {
  ICustomField,
  IItemForSelection,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IStatusForSelection,
  IProjectForSelection,
} from '@/api/types/response.types'
import { SortOptionValues } from '@/types'
import { IItemFiltersPanelState } from '@/types/data-objects/listing'
import { EEntities } from '@/types'

/* API */

export default defineComponent({
  name: 'ItemFiltersPanel',

  props: {
    isKanban: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isDefaultItemTraitSet: {
      type: Boolean,
      default: false,
    },
    isAllTasks: {
      type: Boolean,
      default: false,
    },
    isAllConvos: {
      type: Boolean,
      default: false,
    },
    /* Needed to pull creators in ifp-creator (otherwise pulls from entire account) */
    singleProjectId: {
      type: Number,
      default: null,
    },
    /* Needed to pull customers in ifp-customer (otherwise pulls from entire account) */
    singleMailboxId: {
      type: Number,
      default: null,
    },
    /* Item trait set props */
    searchTerm: {
      type: String,
      required: true,
    },
    selectedEntityIds: {
      type: Array as () => number[],
      default: null,
    },
    selectedAssigneeIds: {
      type: Array as () => number[],
      required: true,
    },
    assignedNotToYou: {
      type: Boolean,
      default: false,
    },
    selectedJobAppointeeGroupingsString: {
      type: String,
      default: '',
    },
    selectedCreatorIds: {
      type: Array as () => number[],
      default: null,
    },
    selectedCustomerIds: {
      type: Array as () => number[],
      default: null,
    },
    selectedStatusIds: {
      type: Array as () => number[],
      required: true,
    },
    statusType: {
      type: String,
      default: '',
    },
    selectedPriorityIds: {
      type: Array as () => number[],
      default: null,
    },
    selectedDueType: {
      type: Number,
      default: null,
    },
    selectedIncludingTagIds: {
      type: Array as () => number[],
      required: true,
    },
    selectedExcludingTagIds: {
      type: Array as () => number[],
      required: true,
    },
    createdWithinDays: {
      type: Number,
      default: undefined,
    },
    lastUpdatedWithinDays: {
      type: Number,
      default: undefined,
    },
    startDueDate: {
      type: String,
      default: '',
    },
    endDueDate: {
      type: String,
      default: '',
    },
    selectedSort: {
      type: String,
      default: SortOptionValues.UPDATED_RECENT,
      // required: true,
    },
    onlyWatching: {
      type: Boolean,
      default: false,
    },
    /* Item trait set props: Convos only */
    isSpam: {
      type: Boolean,
      default: false,
    },
    /* Item trait set props: Single project/mailbox only. Encoded per CustomFieldAssistant.serializeFilters */
    customFieldFiltersString: {
      type: String,
      default: '',
    },
    /* Options for dropdowns */
    entityOptions: {
      type: Array as () => IItemForSelection[],
      default: null,
    },
    assigneeOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },
    jobOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },
    activeStatusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },
    inactiveStatusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },
    tagOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
    dueDateOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
    /* Custom fields the requester can see; only dropdown, date and checkbox fields are offered as filters */
    customFieldOptions: {
      type: Array as () => ICustomField[],
      default: (): ICustomField[] => [],
    },
  },

  emits: [
    'reset-filters',
    'settings',
    'switch-to-kanban',
    'switch-to-list',
    'update:assignedNotToYou',
    'update:createdWithinDays',
    'update:customFieldFiltersString',
    'update:endDueDate',
    'update:isSpam',
    'update:lastUpdatedWithinDays',
    'update:onlyWatching',
    'update:searchTerm',
    'update:selectedAssigneeIds',
    'update:selectedCreatorIds',
    'update:selectedCustomerIds',
    'update:selectedDueType',
    'update:selectedEntityIds',
    'update:selectedExcludingTagIds',
    'update:selectedIncludingTagIds',
    'update:selectedJobAppointeeGroupingsString',
    'update:selectedPriorityIds',
    'update:selectedSort',
    'update:selectedStatusIds',
    'update:startDueDate',
    'update:statusType',
  ],

  data() {
    return {
      SortAssistant,
      /* Custom field chips opened from the picker but without a value yet */
      exposedCustomFieldIDs: [] as number[],
      state: {
        showFilterSpam: false,
        showFilterWatching: false,
        showFilterCreatedWithinDays: false,
        showFilterLastUpdatedWithinDays: false,
        showFilterSort: true,
      } as IItemFiltersPanelState,
    }
  },

  computed: {
    isAll(): boolean {
      return this.isAllTasks || this.isAllConvos
    },
    allEntityType(): EEntities | '' {
      if (this.isAllTasks) {
        return EEntities.PROJECTS
      } else if (this.isAllConvos) {
        return EEntities.MAILBOXES
      }
      return ''
    },
    isSingleMailbox(): boolean {
      return this.singleMailboxId !== null
    },
    isSingleProject(): boolean {
      return this.singleProjectId !== null
    },
    hasCreatedWithinDays(): boolean {
      return this.createdWithinDays !== null && this.createdWithinDays !== 0
    },
    hasLastUpdatedWithinDays(): boolean {
      return (
        this.lastUpdatedWithinDays !== null && this.lastUpdatedWithinDays !== 0
      )
    },
    hasTags(): boolean {
      return (
        this.selectedIncludingTagIds.length > 0 ||
        this.selectedExcludingTagIds.length > 0
      )
    },
    hasJobs(): boolean {
      return this.jobOptions.length > 0
    },
    customFieldFilters(): ICustomFieldFilter[] {
      return CustomFieldAssistant.parseFilters(this.customFieldFiltersString)
    },
    filterableCustomFields(): ICustomField[] {
      return this.customFieldOptions.filter(f =>
        CustomFieldAssistant.isFilterableField(f),
      )
    },
    /* A chip shows when the field has an active filter or was opened from the picker */
    exposedCustomFields(): ICustomField[] {
      return this.filterableCustomFields.filter(
        f =>
          this.exposedCustomFieldIDs.includes(f.id) ||
          this.customFieldFilters.some(cf => cf.fieldID === f.id),
      )
    },
    unexposedCustomFields(): ICustomField[] {
      const exposedIDs = this.exposedCustomFields.map(f => f.id)
      return this.filterableCustomFields.filter(f => !exposedIDs.includes(f.id))
    },
  },

  /* Toggling filter visibility - Read me: 

    Some <ifp-filter-xxx /> components (ifp-filters) are always visible:

      * Assignees
      * Creators
      * Customers
      * Status
      * Priority
      * Due
      * Tags

    ---
    
    Some ifp-filters "show themselves" based on their current respective prop values
    (e.g. <ifp-filter-assignee /> when selectedAssigneeIDs.length > 0). Hence, all ifp-filters emit the update to their
    value to this component, then this component syncs the data back to the parent, which then updates the prop and 
    triggers the watchers below to do the show/hiding by updating its corresponding data.state attribute).

    The <ifp-picker /> also allows a user to expose a hidden ifp-filter manually so the user can start selecting values. The
    corresponding data.state attribute is updated *directly*.  However, if the user ends up reverting a value back to the default
    (i.e. usually "nothing"), then the filter will naturally go away via the watcher. (e.g. User exposes <ifp-filter-assignee /> 
    from the <ifp-picker />, then (1) selects an assignee, then (2) deselects it. The 2nd update will trigger the watcher to 
    hide the ifp-filter.

    Note: These watchers have immediate: true so that the ifp-filter can be exposed on creation if they already have values
    (via the QS, local store, etc.)

      * Created within days
      * Last updated within days
      * Title / ref #
      * You're watching
      * Spam

    The user can also manually close ifp-filters via the "X" button. In most cases, this simply emits the default value
    back to this component, and the normal propogation mentioned above happens.

    In summary, these ifp-filter components:

      a) Show/hide themselves based on the watcher of their corresponding value.
      b) Can manually show from the <ifp-picker /> user action.
      c) Can manually hide from the ifp-filter "X" button (by emitting the default value to this component.)

    ---

    Additionally, in some cases, we want the manual showing of a ifp-filter via <ifp-picker /> to start with a custom value other than
    the default (unless a non-default value already exists), so we can watch on the data.state attribute for when the value becomes
    true (DO NOT SET TO 'immediate: true' so this only fires on a <ifp-picker /> manual update). This is OK because it will never become
    'false' again unless c) happens which sets the value back to the default. Then, showing the filter again sets it back to the 
    custom value.

      1) Created within days --> 1
      2) Last updated within days  -> 1
      3) Spam --> true
      4) Only Watching --> true

    -KC 6/24/22
  */

  watch: {
    createdWithinDays: {
      immediate: true,
      handler(val) {
        this.state.showFilterCreatedWithinDays = this.hasCreatedWithinDays
      },
    },
    lastUpdatedWithinDays: {
      immediate: true,
      handler(val) {
        this.state.showFilterLastUpdatedWithinDays = this.hasLastUpdatedWithinDays
      },
    },
    onlyWatching: {
      immediate: true,
      handler(val) {
        this.state.showFilterWatching = val
      },
    },
    /* Item trait set props: Convos only */
    isSpam: {
      immediate: true,
      handler(val) {
        this.state.showFilterSpam = val
      },
    },
    'state.showFilterCreatedWithinDays': {
      handler(show) {
        if (show && !this.hasCreatedWithinDays) {
          this.$emit('update:createdWithinDays', 1)
        }
      },
    },
    'state.showFilterLastUpdatedWithinDays': {
      handler(show) {
        if (show && !this.hasLastUpdatedWithinDays) {
          this.$emit('update:lastUpdatedWithinDays', 1)
        }
      },
    },
    'state.showFilterSpam': {
      handler(show) {
        if (show) {
          this.$emit('update:isSpam', true)
        }
      },
    },
    'state.showFilterWatching': {
      handler(show) {
        if (show) {
          this.$emit('update:onlyWatching', true)
        }
      },
    },
  },

  methods: {
    handleFilterReset() {
      this.exposedCustomFieldIDs = []
      this.$emit('reset-filters')
    },

    /* ---------- Custom field filters ---------- */

    customFieldFilterFor(field: ICustomField): ICustomFieldFilter | null {
      return this.customFieldFilters.find(cf => cf.fieldID === field.id) || null
    },

    exposeCustomField(fieldID: number) {
      if (!this.exposedCustomFieldIDs.includes(fieldID)) {
        this.exposedCustomFieldIDs.push(fieldID)
      }
    },

    /* Replaces (or removes, when null) this field's entry and syncs the encoded string back to the parent */
    updateCustomFieldFilter(
      field: ICustomField,
      filter: ICustomFieldFilter | null,
    ) {
      const others = this.customFieldFilters.filter(
        cf => cf.fieldID !== field.id,
      )
      const next = filter ? [...others, filter] : others
      const encoded = CustomFieldAssistant.serializeFilters(next)
      if (encoded !== this.customFieldFiltersString) {
        this.$emit('update:customFieldFiltersString', encoded)
      }
    },

    removeCustomFieldFilter(field: ICustomField) {
      this.exposedCustomFieldIDs = this.exposedCustomFieldIDs.filter(
        id => id !== field.id,
      )
      this.updateCustomFieldFilter(field, null)
    },
  },
})
</script>

<style lang="scss">
.item-filters-panel {
  position: relative;
  display: flex;
  align-items: top;
  justify-content: space-between;
  @include flex-row-horizontal-spacing(20px);

  &__disabler {
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    background-color: var(--color-background-primary);
    opacity: 0.7;
  }

  &__loading {
    margin-top: 16px;
    .vue-content-placeholders-text__line {
      width: 100%;
      height: 36px;
      margin: 0;
    }
    @include media-breakpoint-down(sm) {
      padding: 0 $detail-gutter-pad-sm;
      .vue-content-placeholders-text__line {
        height: 24px;
      }
    }
  }

  &__filters-wrapper {
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(7px);
    @include flex-row-vertical-spacing-on-wrap(8px);

    @include media-breakpoint-down(sm) {
      @include flex-row-horizontal-spacing(4px);
      @include flex-row-vertical-spacing-on-wrap(8px);
    }

    > .clear-filters-button {
      margin-left: 4px;
      color: var(--color-text-third);
      text-decoration: underline;
      height: auto;
      padding: 0;
    }
  }

  .other-options {
    display: flex;
    align-self: flex-start;
    @include flex-row-horizontal-spacing(10px);
  }

  @include media-breakpoint-down(sm) {
    padding: 0 $detail-gutter-pad-sm;
  }
}
</style>
